import useSWR from 'swr';

import { fetchEndpointData } from '@utils/fetchEndpointData';

import type { AuditLogListItem, AuditLogsResponse } from './endpoints/AuditLogsEndpoint.types';
import type { AuditLogResponse } from './endpoints/AuditLogEndpoint.types';
import type { UserActivityStatsResponse } from './endpoints/UserActivityStatsEndpoint.types';

export type AuditLog = AuditLogListItem;

export const AUDIT_LOG_SWR_KEY = 'audit-log';

// Mirrors the GraphQL `AuditLogsFilterInput` shape exactly (all optional,
// nullable to match the InputMaybe semantics of the generated type so existing
// callers like `{ orderId }`, `{ customerId }`, etc. keep type-checking).
export interface AuditLogsFilter {
  orderId?: string | null;
  customerId?: number | null;
  supplierId?: number | null;
  locationId?: number | null;
  trailerId?: number | null;
  invoiceId?: string | null;
  supplierSelfBillId?: string | null;
  creditNoteId?: string | null;
  actorId?: string | null;
  actionType?: string | null;
  from?: string | null;
  to?: string | null;
}

export async function getAuditLogs(
  cursor?: string,
  take?: number,
  filter?: AuditLogsFilter | null,
): Promise<AuditLogsResponse> {
  const params = new URLSearchParams();
  if (cursor) params.set('id', cursor);
  if (take != null) params.set('take', take.toString());
  if (filter?.orderId != null) params.set('orderId', filter.orderId);
  if (filter?.customerId != null) params.set('customerId', filter.customerId.toString());
  if (filter?.supplierId != null) params.set('supplierId', filter.supplierId.toString());
  if (filter?.locationId != null) params.set('locationId', filter.locationId.toString());
  if (filter?.trailerId != null) params.set('trailerId', filter.trailerId.toString());
  if (filter?.invoiceId != null) params.set('invoiceId', filter.invoiceId);
  if (filter?.supplierSelfBillId != null) params.set('supplierSelfBillId', filter.supplierSelfBillId);
  if (filter?.creditNoteId != null) params.set('creditNoteId', filter.creditNoteId);
  if (filter?.actorId != null) params.set('actorId', filter.actorId);
  if (filter?.actionType != null) params.set('actionType', filter.actionType);
  if (filter?.from != null) params.set('from', filter.from);
  if (filter?.to != null) params.set('to', filter.to);

  const queryString = params.toString();
  const url = queryString ? `/api/v1/audit-logs?${queryString}` : '/api/v1/audit-logs';
  return fetchEndpointData<AuditLogsResponse>(url);
}

export async function getAuditLog(id: string): Promise<AuditLogResponse> {
  return fetchEndpointData<AuditLogResponse>(`/api/v1/audit-logs/${id}`);
}

export function useAuditLog(id: string | undefined) {
  return useSWR(id ? [AUDIT_LOG_SWR_KEY, id] : null, () => getAuditLog(id!));
}

export interface UserActivityStatsFilters {
  from: string;
  to: string;
  actorId?: string | null;
  actionType?: string | null;
}

export async function fetchUserActivityStats(filters: UserActivityStatsFilters): Promise<UserActivityStatsResponse> {
  const params = new URLSearchParams();
  params.set('from', filters.from);
  params.set('to', filters.to);
  if (filters.actorId) {
    params.set('actorId', filters.actorId);
  }
  if (filters.actionType) {
    params.set('actionType', filters.actionType);
  }
  return fetchEndpointData<UserActivityStatsResponse>(`/api/v1/audit-logs/user-activity-stats?${params.toString()}`);
}

export function useUserActivityStats(filters: UserActivityStatsFilters | null) {
  return useSWR(
    filters ? ['user-activity-stats', filters.from, filters.to, filters.actorId, filters.actionType] : null,
    () => fetchUserActivityStats(filters!),
  );
}
